import React, { useState, useEffect } from 'react';
import { API_URL } from '../../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

interface DJ {
  id: number;
  name: string;
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const AdminProgramForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dj_id: '',
    day_of_week: 'Lunes',
    start_time: '',
    end_time: '',
    active: true,
  });
  const [djs, setDjs] = useState<DJ[]>([]);

  useEffect(() => {
    fetchDJs();
  }, []);

  useEffect(() => {
    if (id) {
      fetchProgram();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDJs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/djs`);
      setDjs(response.data);
    } catch (error) {
      console.error('Error fetching DJs:', error);
    }
  };

  const fetchProgram = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/programs/${id}`);
      setFormData({
        name: response.data.name,
        description: response.data.description || '',
        dj_id: response.data.dj_id || '',
        day_of_week: response.data.day_of_week,
        start_time: response.data.start_time,
        end_time: response.data.end_time,
        active: response.data.active,
      });
    } catch (error) {
      console.error('Error fetching program:', error);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (id) {
        await axios.put(`${API_URL}/api/programs/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/api/programs`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      navigate('/admin/programs');
    } catch (error) {
      console.error('Error saving program:', error);
    }
  };

  return (
    <Paper sx={{ p: 4, background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {id ? 'Editar' : 'Nuevo'} Programa
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Nombre del Programa"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Descripción"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={3}
          sx={{ mb: 3 }}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>DJ</InputLabel>
          <Select
            name="dj_id"
            value={formData.dj_id}
            onChange={handleChange}
            label="DJ"
          >
            <MenuItem value="">Sin DJ</MenuItem>
            {djs.map((dj) => (
              <MenuItem key={dj.id} value={dj.id}>
                {dj.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Día de la Semana</InputLabel>
          <Select
            name="day_of_week"
            value={formData.day_of_week}
            onChange={handleChange}
            label="Día de la Semana"
            required
          >
            {DAYS.map((day) => (
              <MenuItem key={day} value={day}>
                {day}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="Hora de Inicio"
            name="start_time"
            type="time"
            value={formData.start_time}
            onChange={handleChange}
            required
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            fullWidth
            label="Hora de Fin"
            name="end_time"
            type="time"
            value={formData.end_time}
            onChange={handleChange}
            required
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              name="active"
            />
          }
          label="Activo"
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/programs')}
          >
            Volver
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AdminProgramForm;